import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export interface RewardDefinition {
  id: string;
  key: string;
  title: string;
  description: string;
  category: 'ACTIVITY' | 'COMMUNITY' | 'REFERRAL';
  creditValue: number;
  isActive: boolean;
  sortOrder: number;
}

export interface UserReward {
  id: string;
  userId: string;
  rewardDefinitionId: string;
  creditsAwarded: number;
  metadata: Record<string, any> | null;
  earnedAt: string;
  rewardDefinition: RewardDefinition;
}

export interface RewardSummary {
  completedCount: number;
  totalCount: number;
  totalCreditsEarned: number;
  recentRewards: UserReward[];
  nextRewards: RewardDefinition[];
}

export interface ReferralMilestone {
  count: number;
  label: string;
  credits: number;
  achieved: boolean;
}

export interface ReferralStats {
  code: string | null;
  link: string | null;
  totalUses: number;
  validUses: number;
  pendingUses: number;
  totalCreditsEarned: number;
  referrals: {
    id: string;
    referredUser: { id: string; firstName: string | null; lastName: string | null; createdAt: string };
    isValid: boolean;
    creditsAwarded: number;
    createdAt: string;
    validatedAt: string | null;
  }[];
  milestones: ReferralMilestone[];
}

export const rewardsService = {
  async getDefinitions(category?: string): Promise<RewardDefinition[]> {
    const params = category ? { category } : {};
    const { data } = await api.get('/api/rewards/definitions', { params });
    return data.definitions;
  },

  async getMyRewards(): Promise<{ rewards: UserReward[]; totalCreditsEarned: number; count: number }> {
    const { data } = await api.get('/api/rewards/my');
    return data;
  },

  async getSummary(): Promise<RewardSummary> {
    const { data } = await api.get('/api/rewards/summary');
    return data;
  },

  async checkRewards(): Promise<void> {
    await api.post('/api/rewards/check');
  },
};

export const referralsService = {
  async getMyCode(): Promise<{ code: string; link: string; totalUses: number }> {
    const { data } = await api.get('/api/referrals/my-code');
    return data;
  },

  async getStats(): Promise<ReferralStats> {
    const { data } = await api.get('/api/referrals/stats');
    return data;
  },

  async validateCode(code: string): Promise<{ valid: boolean; referrerName?: string }> {
    const { data } = await api.get(`/api/referrals/validate/${code}`);
    return data;
  },

  async applyCode(code: string): Promise<{ success: boolean; message: string }> {
    const { data } = await api.post('/api/referrals/apply', { code });
    return data;
  },

  async validatePending(): Promise<void> {
    await api.post('/api/referrals/validate-pending');
  },

  async getSettings(): Promise<{
    milestones: { count: number; credits: number }[];
    validationDaysRequired: number;
  }> {
    const { data } = await api.get('/api/referrals/settings');
    return data;
  },
};
